import {Component, computed, signal, ChangeDetectionStrategy} from "@angular/core"
import {BaseInspectorComponent} from "@app/template-editor/components/inspectors/base-inspector/base-inspector.component"
import {InspectorSectionComponent} from "@app/template-editor/components/inspectors/inspector-section/inspector-section.component"
import {SelectionPossibilities, ValueSlotComponent} from "@app/template-editor/components/value-slot/value-slot.component"
import {DecalMaskType, MeshDecal, SceneNodes, MaterialAssignment} from "@cm/template-nodes"
import {ButtonComponent} from "@common/components/buttons/button/button.component"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"

const UV_PRECISION = 0.01

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-mesh-decal-inspector",
    templateUrl: "./mesh-decal-inspector.component.html",
    styleUrl: "./mesh-decal-inspector.component.scss",
    imports: [InspectorSectionComponent, ValueSlotComponent, ButtonComponent],
})
export class MeshDecalInspectorComponent extends BaseInspectorComponent<MeshDecal> {
    readonly $mesh = computed(() => this.$parameters().mesh)
    readonly $invertMaskPossibilities = signal<SelectionPossibilities<boolean>>([
        {value: true, name: "White is transparent"},
        {value: false, name: "Black is transparent"},
    ])
    readonly $maskTypePossibilities = signal<SelectionPossibilities<DecalMaskType>>([
        {value: "binary", name: "Binary"},
        {value: "opacity", name: "Opacity"},
    ])

    onRequestUpdate(materialAssignment: unknown) {
        if (materialAssignment instanceof MaterialAssignment) {
            this.sceneManagerService.modifyTemplateGraph(() => this.$node().updateParameters({materialAssignment: materialAssignment}))
        } else if (materialAssignment === null) {
            this.sceneManagerService.modifyTemplateGraph(() => this.$node().updateParameters({materialAssignment: null}))
        }
    }

    chooseUV() {
        const node = this.$node()
        if (!node) return

        const sceneNodeParts = this.sceneManagerService.getSceneNodes(node).filter(SceneNodes.Mesh.is)
        if (sceneNodeParts.length !== 1) return
        const decalMesh = sceneNodeParts[0]
        const mesh = decalMesh.parent
        if (!mesh) return

        this.sceneManagerService
            .watchForClickedSceneNodePart()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((sceneNodePartClickEvent) => {
                const meshIntersection = sceneNodePartClickEvent.target.find(({sceneNodePart}) => sceneNodePart.sceneNode.id === mesh.id)

                if (!meshIntersection) return

                const {intersection} = meshIntersection
                if (!intersection) return

                const {uv} = intersection
                if (!uv) return

                const {x, y} = uv

                const u = Math.round(x / UV_PRECISION) * UV_PRECISION
                const v = Math.round(y / UV_PRECISION) * UV_PRECISION

                this.sceneManagerService.modifyTemplateGraph(() => {
                    this.$node().updateParameters({offset: [u, v]})
                })
            })
    }
}
